// Shared component (TS-2.5.03). Owner ruling 2026-10-03, step two: the FIVE OPEN fields (project
// Lead, project Business owner, product Owner, and the Original/Current go-live dates) stay free
// text - a person's name or a messy date can be anything - so none of them becomes a VocabularySelect
// (see that component's header for the CLOSED fields this is deliberately not one of). Each used
// to be an `<input list=...>` + `<datalist>` pair, and a browser datalist filters its suggestions
// by the control's CURRENT TEXT: once a value is already typed in, the user sees only that one
// value echoed back and has no way to discover what else the list offers - the same invisible-
// vocabulary bug VocabularySelect fixed for the CLOSED fields, but free text rules out a
// `<select>` fix here (D-011/D-249 both require that whatever is typed keeps being accepted,
// unlisted or not). This is the shared fix: one accessible combobox that shows every suggestion on
// request regardless of what is already typed, while still accepting anything.
//
// Built to the WAI-ARIA 1.2 "Editable Combobox With List Autocomplete" pattern (the same rigour
// AdminPage.tsx's tab bar follows for its own pattern, D-244):
//   - the text field itself carries role="combobox", aria-autocomplete="list", aria-expanded,
//     aria-controls and aria-activedescendant;
//   - a non-tabbable (tabIndex=-1) toggle button, and ArrowDown/ArrowUp/Alt+ArrowDown on the field
//     itself, all open the FULL list - never filtered by the current text - with the option
//     matching the current value (case-insensitive, by its underlying value) marked aria-selected
//     and scrolled into view;
//   - typing instead filters the list to options whose value or label contains the typed text
//     (case-insensitive substring), as a `<datalist>` would, but opens every time so the user
//     always sees what matched, not just an echo of what they typed;
//   - ArrowDown/ArrowUp move the active option (opening the full list first if the popup was
//     closed); Enter picks the active option; Escape closes without changing the field; Tab closes
//     and moves focus on, exactly as a native field would, without picking anything; clicking an
//     option picks it, clicking anywhere else closes the popup.
// Picking writes the suggestion's `value` - the data a caller's draft actually stores (e.g. a date
// placeholder's literal token) - never its optional `label`, which exists only to show a friendlier
// string than the stored value (mirroring a `<datalist>` option's own value/label split). `hint` is
// a second, optional annotation shown beside the label (e.g. "(4)" for a suggested owner's project
// count) that is never written back.
import { useEffect, useMemo, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent } from "react";
import { formFieldControlProps } from "./formFieldControlProps";
import { strings } from "../strings";
import "./suggestionInput.css";

export interface SuggestionInputOption {
  readonly value: string;
  readonly label?: string;
  readonly hint?: string;
}

export interface SuggestionInputProps {
  readonly id: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly suggestions: readonly SuggestionInputOption[];
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly description?: string;
  readonly error?: string | null;
}

const S = strings.common;

function displayText(option: SuggestionInputOption): string {
  return option.label ?? option.value;
}

/** The active option a stored value means: its own `value`, trimmed and case-insensitive - never
 *  its `label`, which may read quite differently from what is actually stored (a date placeholder
 *  token vs. its friendly description). */
function indexOfCurrentValue(suggestions: readonly SuggestionInputOption[], value: string): number | null {
  const folded = value.trim().toLowerCase();
  if (folded.length === 0) return null;
  const index = suggestions.findIndex((option) => option.value.trim().toLowerCase() === folded);
  return index === -1 ? null : index;
}

function matchesFilter(option: SuggestionInputOption, text: string): boolean {
  const folded = text.trim().toLowerCase();
  if (folded.length === 0) return true;
  return option.value.toLowerCase().includes(folded) || displayText(option).toLowerCase().includes(folded);
}

export function SuggestionInput({
  id,
  value,
  onChange,
  suggestions,
  placeholder,
  required,
  description,
  error,
}: SuggestionInputProps) {
  const [open, setOpen] = useState(false);
  // true while the popup should show every suggestion regardless of the current text (opened via
  // the toggle, ArrowDown/ArrowUp from closed, or Alt+ArrowDown) - false while it is filtering by
  // what the user is typing.
  const [browseAll, setBrowseAll] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(
    () => (browseAll ? suggestions : suggestions.filter((option) => matchesFilter(option, value))),
    [browseAll, suggestions, value],
  );

  useEffect(() => {
    if (!open || activeIndex === null) return;
    const activeElement = listboxRef.current?.children[activeIndex];
    if (activeElement && typeof (activeElement as HTMLElement).scrollIntoView === "function") {
      (activeElement as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [open, activeIndex]);

  const listboxId = `${id}-listbox`;

  function openShowingAll() {
    setBrowseAll(true);
    setOpen(true);
    setActiveIndex(indexOfCurrentValue(suggestions, value));
  }

  function pick(option: SuggestionInputOption) {
    onChange(option.value);
    setOpen(false);
    setBrowseAll(false);
    setActiveIndex(null);
    inputRef.current?.focus();
  }

  function handleInputChange(next: string) {
    onChange(next);
    setBrowseAll(false);
    setOpen(true);
    setActiveIndex(null);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" && event.altKey) {
      event.preventDefault();
      openShowingAll();
      return;
    }

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        if (!open) {
          openShowingAll();
          return;
        }
        setActiveIndex((previous) => Math.min((previous ?? -1) + 1, filtered.length - 1));
        return;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (!open) {
          openShowingAll();
          return;
        }
        setActiveIndex((previous) => Math.max((previous ?? filtered.length) - 1, 0));
        return;
      }
      case "Enter": {
        if (open && activeIndex !== null && filtered[activeIndex]) {
          event.preventDefault();
          pick(filtered[activeIndex]);
        }
        return;
      }
      case "Escape": {
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        return;
      }
      case "Tab": {
        setOpen(false);
        return;
      }
      default:
        return;
    }
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!containerRef.current?.contains(event.relatedTarget as Node | null)) {
      setOpen(false);
    }
  }

  function handleToggleClick() {
    if (open) {
      setOpen(false);
    } else {
      openShowingAll();
    }
    inputRef.current?.focus();
  }

  return (
    <div className="eg-suggestion-input" ref={containerRef} onBlur={handleBlur}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open && activeIndex !== null ? `${id}-option-${activeIndex}` : undefined}
        value={value}
        placeholder={placeholder}
        required={required}
        onChange={(event) => handleInputChange(event.target.value)}
        onKeyDown={handleKeyDown}
        {...formFieldControlProps(id, description, error)}
      />
      <button
        type="button"
        className="eg-suggestion-input__toggle"
        aria-label={S.suggestionsToggle}
        tabIndex={-1}
        onMouseDown={(event) => event.preventDefault()}
        onClick={handleToggleClick}
      >
        ▾
      </button>
      {open && (
        <ul id={listboxId} role="listbox" className="eg-suggestion-input__listbox" ref={listboxRef}>
          {filtered.map((option, index) => (
            <li
              key={option.value}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? "eg-suggestion-input__option--active" : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(option)}
            >
              {displayText(option)}
              {option.hint && <span className="eg-suggestion-input__hint"> {option.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
